import type { Ecom } from '@serato/sws-sdk'
import {
  APPLE_MULTISUB_BLOCK_ERROR_CODE_ACTIVE,
  APPLE_MULTISUB_BLOCK_ERROR_CODE_PAST_DUE_OR_CANCELLED_RETRY,
  APPLE_SUBSCRIPTION_WARNING_CODE_ACTIVE,
  APPLE_SUBSCRIPTION_WARNING_CODE_PAST_DUE_OR_CANCELLED_RETRY,
  CART_ERROR_CODE_EXISTING_SUBSCRIPTION,
  CART_ERROR_CODE_INELIGIBLE_PNT_UPGRADE
} from '@/utils/const'

export type CartWarningContext = 'pre-checkout' | 'post-checkout'

export interface CartCheckoutAlertContent {
  header: string
  message: string
  buttonText: string
  buttonHref: string
}

const MY_ACCOUNT_SUBS_URL = import.meta.env.VITE_APP_CLIENT_URI_MY_SERATO_COM + '/#/subscriptions'
const PITCH_N_TIME_URL = import.meta.env.VITE_APP_CLIENT_URI_SERATO_COM + '/pitchntime'

type WarningMessageByContext = Partial<Record<CartWarningContext, string>>

const CART_WARNING_MESSAGES: Record<number, WarningMessageByContext> = {
  [APPLE_SUBSCRIPTION_WARNING_CODE_ACTIVE]: {
    'pre-checkout':
      'Heads up - this account has an active subscription with NewSubscriptionGateway. When you complete this transaction, you will have two subscriptions. To avoid being billed twice, you’ll need to cancel one of your subscriptions.',
    'post-checkout':
      'Heads up - this account has active subscriptions with Serato and NewSubscriptionGateway. To avoid being billed twice, you’ll need to cancel one of your subscriptions.'
  },
  [APPLE_SUBSCRIPTION_WARNING_CODE_PAST_DUE_OR_CANCELLED_RETRY]: {
    'pre-checkout':
      'Heads up - this account has an NewSubscriptionGateway subscription. NewSubscriptionGateway will attempt to restart your subscription. Visit My Account to view details.',
    'post-checkout':
      'Heads up - this account has an NewSubscriptionGateway subscription. NewSubscriptionGateway will attempt to restart your subscription. Visit My Account to view details.'
  }
}

const getProductFamily = (productTypeName: string): string => {
  const productType = productTypeName.toLowerCase()
  if (productType.includes('dj')) {
    return 'Serato DJ'
  }
  if (productType.includes('producer suite')) {
    return 'Serato Producer Suite'
  }
  return 'Serato'
}

const existingSubscriptionMessage = (cartItem: Ecom.CartItem): string => {
  const productFamily = getProductFamily(cartItem.product_type.name)
  return `Heads up - this account has an active ${productFamily} subscription. Visit My Account to view details.`
}

export const getCartWarningMessage = (
  warningCode: number | undefined | null,
  context: CartWarningContext
): string | null => {
  if (warningCode === undefined || warningCode === null) {
    return null
  }

  return CART_WARNING_MESSAGES[warningCode]?.[context] ?? null
}

export const hasCartWarningMessage = (
  warningCode: number | undefined | null,
  context: CartWarningContext
): boolean => getCartWarningMessage(warningCode, context) !== null

export const shouldPersistPostCheckoutWarning = (warningCode?: number): boolean =>
  hasCartWarningMessage(warningCode, 'post-checkout')

export const getPreCheckoutWarningAlert = (
  warningCode: number | undefined
): CartCheckoutAlertContent | null => {
  const message = getCartWarningMessage(warningCode, 'pre-checkout')
  if (!message) {
    return null
  }

  return {
    header: 'Existing subscription found',
    message,
    buttonText: 'Manage',
    buttonHref: MY_ACCOUNT_SUBS_URL
  }
}

export const getCartErrorAlert = (
  errorCode: number | undefined | null,
  cartItem?: Ecom.CartItem
): CartCheckoutAlertContent | null => {
  if (errorCode === undefined || errorCode === null || !cartItem) {
    return null
  }

  switch (errorCode) {
  case CART_ERROR_CODE_EXISTING_SUBSCRIPTION:
    return {
      header: 'Existing subscription found',
      message: existingSubscriptionMessage(cartItem),
      buttonText: 'Manage',
      buttonHref: MY_ACCOUNT_SUBS_URL
    }
  case CART_ERROR_CODE_INELIGIBLE_PNT_UPGRADE:
    return {
      header: 'Upgrade not available',
      message: 'Sorry, there’s no Pitch ’n Time upgrade available for this account.',
      buttonText: 'Buy Pitch ’n Time',
      buttonHref: PITCH_N_TIME_URL
    }
  case APPLE_MULTISUB_BLOCK_ERROR_CODE_ACTIVE:
    return {
      header: 'Existing subscriptions found',
      message:
        'Heads up - this account has active subscriptions with Serato and NewSubscriptionGateway. Visit My Account to view details.',
      buttonText: 'Manage',
      buttonHref: MY_ACCOUNT_SUBS_URL
    }
  case APPLE_MULTISUB_BLOCK_ERROR_CODE_PAST_DUE_OR_CANCELLED_RETRY:
    return {
      header: 'Existing subscriptions found',
      message:
        'Heads up - this account has active subscriptions with Serato and NewSubscriptionGateway. NewSubscriptionGateway will attempt to restart your subscription. Visit My Account to view details.',
      buttonText: 'Manage',
      buttonHref: MY_ACCOUNT_SUBS_URL
    }
  default:
    return null
  }
}
